import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
    const { user, getAuthHeader } = useAuth();
    const [planData, setPlanData] = useState(null);
    const [tiers, setTiers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // 'plan_limit' (HTTP 403/402 with plan_limit_reached/feature_not_available)
    // vs 'connection' (HTTP 500, timeout, network error) vs null.
    const [errorType, setErrorType] = useState(null);

    const retryCountRef = useRef(0);
    const retryTimerRef = useRef(null);

    // Max auto-retries for connection errors, delay between retries in ms.
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 3000;

    // Determine whether an axios error is a transient connection failure
    // (server error, timeout, or network error) vs a plan-limit error
    // (403/402 with plan_limit_reached/feature_not_available in the detail).
    const classifyError = useCallback((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const isPlanLimit =
            (status === 403 || status === 402) &&
            detail && typeof detail === 'object' &&
            (detail.error === 'plan_limit_reached' || detail.error === 'feature_not_available');
        if (isPlanLimit) return 'plan_limit';
        // No response at all → network error / timeout. 5xx → server error.
        if (!err?.response || (status && status >= 500)) return 'connection';
        return 'connection';
    }, []);

    const loadPlan = useCallback(async (opts = {}) => {
        const isRetry = opts.retry === true;
        if (!user) { setLoading(false); return; }
        try {
            const [planRes, tiersRes] = await Promise.all([
                axios.get(`${API}/plans/current`, { headers: getAuthHeader() }),
                axios.get(`${API}/plans/tiers`),
            ]);
            setPlanData(planRes.data);
            setTiers(tiersRes.data);
            setError(null);
            setErrorType(null);
            retryCountRef.current = 0;
        } catch (err) {
            const type = classifyError(err);
            setError(err);
            setErrorType(type);
            // Auto-retry connection errors (up to MAX_RETRIES).
            if (type === 'connection' && !isRetry && retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current += 1;
                if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                retryTimerRef.current = setTimeout(() => {
                    loadPlan({ retry: true });
                }, RETRY_DELAY_MS);
            }
        } finally {
            setLoading(false);
        }
    }, [user, getAuthHeader, classifyError]);

    useEffect(() => { loadPlan(); }, [loadPlan]);

    // Cleanup any pending retry timer on unmount.
    useEffect(() => {
        return () => {
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        };
    }, []);

    // Manual retry: resets the retry counter and re-runs loadPlan immediately.
    const retry = useCallback(() => {
        retryCountRef.current = 0;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        loadPlan({ retry: true });
    }, [loadPlan]);

    // Fail-closed on BOTH axes.
    //
    // `!== false` used to be the test, which granted any key the payload did
    // not carry: `undefined !== false` is `true`. The backend does the
    // opposite — `routes/plans.py::check_feature` is
    // `plan["features"].get(feature, False)` — so every feature key added on
    // the server before this payload learned to send it was denied by the API
    // and *granted* by the UI. Users saw a nav item that 403'd when clicked.
    //
    // `planData` missing stays a denial too, but callers should consult
    // `loading` first: "not loaded yet" and "not entitled" are different states
    // and rendering the second while in the first is what makes nav items
    // flicker out on every page load.
    const checkFeature = useCallback((feature) => {
        if (!planData) return false;
        return planData.features?.[feature] === true;
    }, [planData]);

    const checkLimit = useCallback((resource) => {
        if (!planData) return { allowed: false, current: 0, limit: 0, unlimited: false, percentage: 0 }; // Fail-closed
        const usage = planData.usage?.[resource];
        if (!usage) return { allowed: false, current: 0, limit: 0, unlimited: false, percentage: 0 }; // Fail-closed
        return {
            allowed: usage.unlimited || usage.current < usage.limit,
            current: usage.current ?? 0,
            limit: usage.limit ?? 0,
            unlimited: usage.unlimited,
            percentage: usage.percentage ?? 0,
        };
    }, [planData]);

    const enabledModules = planData?.enabled_modules || [];

    const checkModule = useCallback((moduleKey) => {
        if (!planData) return false;
        return (planData.enabled_modules || []).includes(moduleKey);
    }, [planData]);

    const value = {
        plan: planData?.plan ?? (loading ? null : 'free'),
        planLabel: planData?.plan_label ?? (loading ? null : 'Free'),
        planData,
        tiers,
        loading,
        error,
        errorType,
        isConnectionError: errorType === 'connection',
        clearError: () => { setError(null); setErrorType(null); },
        retry,
        checkFeature,
        // "We have not been told yet." Distinct from `checkFeature() === false`,
        // which means "we were told no". Consumers that render a denial UI must
        // branch on this first.
        planUnknown: loading || !planData,
        checkLimit,
        checkModule,
        enabledModules,
        refreshPlan: loadPlan,
    };

    return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan() {
    const ctx = useContext(PlanContext);
    if (!ctx) return { plan: 'free', planLabel: 'Free', planUnknown: true, checkFeature: () => false, checkLimit: () => ({ allowed: false, current: 0, limit: 0, unlimited: false, percentage: 0 }), checkModule: () => false, enabledModules: [], loading: true, error: null, errorType: null, isConnectionError: false, retry: () => {}, clearError: () => {} };
    return ctx;
}
